import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge, useReactFlow, MarkerType, ConnectionMode,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TechniqueNode from "@/components/TechniqueNode";
import FlowEdge from "@/components/FlowEdge";
import TechniqueDetailDialog from "@/components/TechniqueDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import RichTextEditor from "@/components/RichTextEditor";
import { TechniquePalette } from "@/components/TechniquePalette";
import { TechniqueCommandPalette } from "@/components/TechniqueCommandPalette";
import { useIsTouch } from "@/hooks/use-is-touch";
import { useVisualViewport } from "@/hooks/use-visual-viewport-height";
import { ArrowLeft, Save, Plus, Spline, ArrowLeftRight, PanelLeftClose, PanelLeftOpen, Command as CommandIcon } from "lucide-react";
import { toast } from "sonner";

const nodeTypes = { technique: TechniqueNode };
const edgeTypes = { flow: FlowEdge };
const ARROW = { type: MarkerType.ArrowClosed, color: "#E63946", width: 18, height: 18 };

function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatform = user?.role === "platform_admin";
  const canGym = (user?.role === "admin" || user?.role === "coach") && user?.gym_id;

  const isTouch = useIsTouch();
  const wrapperRef = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [techniques, setTechniques] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [meta, setMeta] = useState({ name: "", description: "", scope: user?.role === "platform_admin" ? "platform" : "personal" });
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(() => {
    try { return localStorage.getItem("builder.paletteCollapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("builder.paletteCollapsed", paletteCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [paletteCollapsed]);
  const [connectMode, setConnectMode] = useState(false);
  const { height: vvh, offsetTop: vvTop, keyboardInset } = useVisualViewport();
  const [connectSource, setConnectSource] = useState(null);
  const [twoWay, setTwoWay] = useState(false);
  const idCounter = useRef(1);
  const edgeCounter = useRef(1);
  const connectModeRef = useRef(false);
  useEffect(() => { connectModeRef.current = connectMode; if (!connectMode) setConnectSource(null); }, [connectMode]);

  // While the mobile sheet is open, pin the document scroll so iOS Safari's
  // focus-scroll can't pan the page (the search input is at the top and never
  // needs to be scrolled into view).
  useEffect(() => {
    if (!sheetOpen) return;
    const pin = () => {
      if (window.scrollY || document.documentElement.scrollTop) window.scrollTo(0, 0);
    };
    pin();
    window.addEventListener("scroll", pin);
    window.visualViewport?.addEventListener("resize", pin);
    return () => {
      window.removeEventListener("scroll", pin);
      window.visualViewport?.removeEventListener("resize", pin);
    };
  }, [sheetOpen]);

  const openPreview = useCallback((t) => { if (connectModeRef.current) return; setPreview(t); }, []);
  const removeNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const decorate = useCallback((n) => ({
    ...n,
    type: "technique",
    data: { ...n.data, onRemove: () => removeNode(n.id), onOpen: openPreview },
  }), [removeNode, openPreview]);

  const deleteEdge = useCallback((eid) => setEdges((eds) => eds.filter((e) => e.id !== eid)), [setEdges]);
  const toggleEdge = useCallback((eid) => setEdges((eds) => eds.map((e) => {
    if (e.id !== eid) return e;
    const bi = !e.data?.bidirectional;
    return { ...e, markerStart: bi ? ARROW : undefined, data: { ...e.data, bidirectional: bi } };
  })), [setEdges]);

  const decorateEdge = useCallback((e) => ({
    ...e,
    type: "flow",
    animated: true,
    markerEnd: ARROW,
    markerStart: e.data?.bidirectional ? ARROW : undefined,
    data: { ...e.data, bidirectional: !!e.data?.bidirectional, onDelete: deleteEdge, onToggle: toggleEdge },
  }), [deleteEdge, toggleEdge]);

  // Load techniques for the side panel
  useEffect(() => {
    api.get("/techniques", { params: q ? { q } : {} }).then(({ data }) => setTechniques(data)).catch(() => {});
  }, [q]);

  // Load existing sequence
  useEffect(() => {
    if (!id) return;
    api.get(`/sequences/${id}`).then(({ data }) => {
      setMeta({ name: data.name, description: data.description || "", scope: data.scope });
      const loaded = (data.nodes || []).map((n) => decorate(n));
      setNodes(loaded);
      setEdges((data.edges || []).map((e) => decorateEdge({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, data: { bidirectional: !!e.bidirectional } })));
      const maxId = Math.max(0, ...(data.nodes || []).map((n) => parseInt(n.id.replace(/\D/g, "")) || 0));
      idCounter.current = maxId + 1;
    }).catch((e) => { toast.error(apiError(e)); navigate("/sequences"); });
    // eslint-disable-next-line
  }, [id]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(decorateEdge(params), eds)), [setEdges, decorateEdge]);

  const addTechnique = useCallback((t, position) => {
    const nodeId = `n${idCounter.current++}`;
    const node = decorate({ id: nodeId, position, data: { technique: t } });
    setNodes((nds) => [...nds, node]);
  }, [decorate, setNodes]);

  const onDragStart = (e, t) => { e.dataTransfer.setData("application/bjj", JSON.stringify(t)); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/bjj");
    if (!raw) return;
    const t = JSON.parse(raw);
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    addTechnique(t, position);
  }, [screenToFlowPosition, addTechnique]);

  const clickAdd = (t) => {
    const count = nodes.length;
    addTechnique(t, { x: 80 + (count % 4) * 240, y: 80 + Math.floor(count / 4) * 200 });
    setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
  };

  // Mobile tap-to-connect: tap a source node, then a target node, to draw an edge.
  // Attaches the line to the nearest logical sides so it's clear where it connects,
  // and blocks duplicate connections between the same two nodes.
  const pickHandles = (src, tgt) => {
    const sw = src.measured?.width ?? 224, sh = src.measured?.height ?? 168;
    const tw = tgt.measured?.width ?? 224, th = tgt.measured?.height ?? 168;
    const dx = (tgt.position.x + tw / 2) - (src.position.x + sw / 2);
    const dy = (tgt.position.y + th / 2) - (src.position.y + sh / 2);
    if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? { sourceHandle: "right", targetHandle: "left" } : { sourceHandle: "left", targetHandle: "right" };
    return dy > 0 ? { sourceHandle: "bottom", targetHandle: "top" } : { sourceHandle: "top", targetHandle: "bottom" };
  };
  const onNodeClick = useCallback((_, node) => {
    if (!connectMode) return;
    if (!connectSource) { setConnectSource(node.id); return; }
    if (connectSource === node.id) { setConnectSource(null); return; }
    const src = nodes.find((n) => n.id === connectSource);
    if (!src) { setConnectSource(null); return; }
    const dup = edges.some((e) => (e.source === src.id && e.target === node.id) || (e.source === node.id && e.target === src.id));
    if (dup) { toast("Those techniques are already connected"); setConnectSource(null); return; }
    const { sourceHandle, targetHandle } = pickHandles(src, node);
    const eid = `te${edgeCounter.current++}`;
    setEdges((eds) => addEdge(decorateEdge({ id: eid, source: src.id, target: node.id, sourceHandle, targetHandle, data: { bidirectional: twoWay } }), eds));
    setConnectSource(null);
    toast.success(twoWay ? "Two-way connection added" : "Connection added");
  }, [connectMode, connectSource, nodes, edges, twoWay, setEdges, decorateEdge]);

  const connectSourceName = connectSource ? (nodes.find((n) => n.id === connectSource)?.data?.technique?.name || "technique") : null;

  // Highlight the node currently selected as the connection source.
  useEffect(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, className: n.id === connectSource ? "ring-2 ring-primary ring-offset-2 ring-offset-background rounded-xl" : "" })));
  }, [connectSource, setNodes]);

  // Global Cmd/Ctrl+K opens the technique quick-add palette.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (isTouch) setSheetOpen(true);
        else setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isTouch]);

  const handleSave = async () => {
    if (!meta.name.trim()) { toast.error("Give your sequence a name"); return; }
    setSaving(true);
    try {
      const payloadNodes = nodes.map((n) => ({ id: n.id, position: n.position, data: { technique: n.data.technique } }));
      const payloadEdges = edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, bidirectional: !!e.data?.bidirectional }));
      const payload = { ...meta, nodes: payloadNodes, edges: payloadEdges };
      if (id) await api.put(`/sequences/${id}`, payload);
      else await api.post("/sequences", payload);
      toast.success("Sequence saved");
      navigate("/sequences");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="h-14 flex items-center gap-2 px-3 border-b border-border bg-black/60 backdrop-blur-xl shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Button data-testid="builder-back-btn" variant="ghost" size="sm" onClick={() => navigate("/sequences")} className="text-muted-foreground hover:text-white shrink-0 px-2 h-10 min-w-10 sm:h-8 sm:min-w-0">
            <ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Exit</span>
          </Button>
          <Input data-testid="builder-name-input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })}
            placeholder="Untitled sequence" className="h-9 min-w-0 flex-1 max-w-64 bg-transparent border-border font-heading font-semibold" />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isTouch && (
            <Button data-testid="builder-cmdk-btn" size="sm" variant="outline" onClick={() => setCmdOpen(true)} className="border-border rounded px-2.5 gap-1.5">
              <CommandIcon className="h-3.5 w-3.5" /> <span className="hidden lg:inline text-xs">Add technique</span>
              <kbd className="hidden md:inline-flex items-center gap-0.5 text-[10px] text-muted-foreground border border-border rounded px-1 py-0.5 ml-1">⌘K</kbd>
            </Button>
          )}
          {isTouch && (
            <Button data-testid="mobile-connect-toggle" size="sm" onClick={() => setConnectMode((v) => !v)}
              variant={connectMode ? "default" : "outline"}
              className={connectMode ? "bg-primary hover:bg-primary/90 text-white rounded px-2.5 h-10 sm:h-8" : "border-border rounded px-2.5 h-10 sm:h-8"}>
              <Spline className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{connectMode ? "Connecting" : "Connect"}</span>
            </Button>
          )}
          <Button data-testid="builder-save-btn" size="sm" onClick={() => setSaveOpen(true)} className="bg-primary hover:bg-primary/90 text-white rounded px-3 h-10 sm:h-8">
            <Save className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Save</span>
          </Button>
        </div>
      </div>

      {!isTouch && (
        <div className="flex items-center gap-4 px-4 py-1.5 text-[11px] text-muted-foreground border-b border-border bg-[#0c0c0c]">
          <span>Drag from a node's red dot to another node to connect</span>
          <span className="text-zinc-600">•</span>
          <span>Click a line to delete it or toggle one-way / two-way arrows</span>
        </div>
      )}

      {isTouch && connectMode && (
        <div data-testid="connect-hint" className="flex items-center justify-between gap-2 px-4 py-2 text-xs bg-primary/10 border-b border-primary/30">
          <div className="flex items-center gap-2 min-w-0">
            {connectSource ? (
              <>
                <span className="text-primary font-medium truncate">Tap the next move to link from <span className="font-semibold">“{connectSourceName}”</span></span>
                <button data-testid="connect-cancel" onClick={() => setConnectSource(null)} className="text-muted-foreground underline shrink-0">cancel</button>
              </>
            ) : (
              <span className="text-primary font-medium">Tap a technique to start a connection</span>
            )}
          </div>
          <button data-testid="mobile-twoway-toggle" onClick={() => setTwoWay((v) => !v)}
            className="flex items-center gap-1 rounded-full border border-primary/50 px-2.5 py-1 text-primary shrink-0">
            <ArrowLeftRight className="h-3 w-3" /> {twoWay ? "Two-way" : "One-way"}
          </button>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Technique panel (desktop / non-touch) */}
        {!isTouch && (
          paletteCollapsed ? (
            <aside data-testid="palette-rail" className="flex w-12 border-r border-border bg-[#0c0c0c] flex-col shrink-0 items-center py-3 gap-2">
              <button
                data-testid="palette-expand-btn"
                onClick={() => setPaletteCollapsed(false)}
                title="Show technique list"
                className="h-9 w-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
              <button
                data-testid="palette-cmdk-btn"
                onClick={() => setCmdOpen(true)}
                title="Quick add (⌘K)"
                className="h-9 w-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
              <div className="mt-auto text-[9px] uppercase tracking-wider text-muted-foreground/60 [writing-mode:vertical-rl] rotate-180 select-none">
                ⌘K search
              </div>
            </aside>
          ) : (
            <aside data-testid="palette-sidebar" className="flex w-72 border-r border-border bg-[#0c0c0c] flex-col shrink-0">
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Drag onto canvas</p>
                <button
                  data-testid="palette-collapse-btn"
                  onClick={() => setPaletteCollapsed(true)}
                  title="Collapse panel"
                  className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/5"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 min-h-0 pt-2">
                <TechniquePalette
                  techniques={techniques}
                  q={q}
                  setQ={setQ}
                  catFilter={catFilter}
                  setCatFilter={setCatFilter}
                  onSelect={clickAdd}
                  onDragStart={onDragStart}
                  variant="desktop"
                  searchTestid="builder-search-input"
                  filterTestid="builder-cat-filter"
                />
              </div>
            </aside>
          )
        )}

        {/* Canvas */}
        <div ref={wrapperRef} className="flex-1 relative" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodesDraggable={!connectMode} nodesConnectable={!isTouch}
            nodeTypes={nodeTypes} edgeTypes={edgeTypes} connectionMode={ConnectionMode.Loose}
            colorMode="dark" fitView proOptions={{ hideAttribution: true }}
            deleteKeyCode={["Backspace", "Delete"]}
          >
            <Background color="#27272A" gap={20} />
            <Controls />
            {!isTouch && <MiniMap nodeColor="#E63946" maskColor="rgba(0,0,0,0.7)" />}
          </ReactFlow>
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
              <div className="text-center">
                <p className="font-heading text-lg text-muted-foreground">{isTouch ? `Tap "Add technique" to place moves` : "Drag techniques here"}</p>
                <p className="text-sm text-muted-foreground/70 mt-1">{isTouch ? `then tap "Connect" to link them together` : `then drag from a node's edge dot to another to connect them`}</p>
              </div>
            </div>
          )}
          {isTouch && (
            <Button data-testid="mobile-add-btn" onClick={() => setSheetOpen(true)}
              className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg px-5 h-11">
              <Plus className="h-4 w-4 mr-1.5" /> Add technique
            </Button>
          )}
        </div>
      </div>

      {/* Technique palette (mobile) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen} modal={false}>
        <SheetContent
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="bg-[#0c0c0c] border-border w-screen max-w-[100vw] flex flex-col gap-0 p-4 overflow-hidden"
          style={{
            top: 0,
            bottom: 0,
            height: "auto",
            paddingTop: 16,
            paddingBottom: 16,
            transition: "none !important",
          }}
          data-testid="mobile-palette-sheet"
        >
          <SheetHeader className="shrink-0"><SheetTitle className="font-heading text-left">Add a technique</SheetTitle></SheetHeader>
          {typeof window !== "undefined" && window.location.search.includes("vvdebug") && (
            <div className="shrink-0 text-[10px] font-mono text-amber-400" data-testid="vv-debug">
              vvh {Math.round(vvh)} · top {Math.round(vvTop)} · kb {Math.round(keyboardInset)} · inner {window.innerHeight}
            </div>
          )}
          <div className="flex-1 min-h-0 min-w-0 mt-3">
            <TechniquePalette
              techniques={techniques}
              q={q}
              setQ={setQ}
              catFilter={catFilter}
              setCatFilter={setCatFilter}
              onSelect={(t) => { clickAdd(t); setSheetOpen(false); }}
              variant="mobile"
              searchTestid="mobile-search-input"
              filterTestid="mobile-cat-filter"
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Cmd/Ctrl+K quick-add palette (desktop) */}
      <TechniqueCommandPalette
        open={cmdOpen}
        onOpenChange={setCmdOpen}
        techniques={techniques}
        onSelect={clickAdd}
      />


      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="save-sequence-dialog">
          <DialogHeader><DialogTitle className="font-heading">Save sequence</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label>
              <Input data-testid="save-name-input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="mt-1.5 bg-background border-border" placeholder="Closed guard attacks" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Description</label>
              <div className="mt-1.5">
                <RichTextEditor
                  testid="save-description-input"
                  value={meta.description}
                  onChange={(html) => setMeta({ ...meta, description: html })}
                  placeholder="Optional — paste from ChatGPT/Gemini keeps formatting."
                  minHeight={140}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Visibility</label>
              <Select value={meta.scope} onValueChange={(v) => setMeta({ ...meta, scope: v })}>
                <SelectTrigger data-testid="save-scope-select" className="mt-1.5 bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isPlatform ? (
                    <SelectItem value="platform">🌐 Platform (everyone)</SelectItem>
                  ) : <>
                    <SelectItem value="personal">👤 Personal (private)</SelectItem>
                    {canGym && <SelectItem value="gym">🏫 Gym (shared)</SelectItem>}
                  </>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="confirm-save-sequence-btn" onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white w-full">{saving ? "Saving…" : "Save sequence"}</Button>
        </DialogContent>
      </Dialog>

      {/* Node detail (matches Techniques page) */}
      <TechniqueDetailDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} technique={preview} />
    </div>
  );
}

export default function SequenceBuilder() {
  return <ReactFlowProvider><Builder /></ReactFlowProvider>;
}
